<template>
  <div>
    <div class="h-screen flex" v-if="isPageLoading">
      <base-loading spinnerColor="primary" size="26" />
    </div>
    <div class="card-box-no-shadow relative" v-else>
      <div class="flex justify-start items-center">
        <div class="back-button" @click="$router.go(-1)">
          <back-arrow class="m-auto right-back-arrow" />
        </div>
        <div class="px-20 sm:px-10">
          <div class="page-title">
            {{ deviceId ? $t('edit_device') : $t('add_device') }}
          </div>
        </div>
      </div>
      <div class="container-padding">
        <div>
          <div class="specification-section">
            <div class="flex justify-between md:flex-col
             sm:flex-col lg:flex-col flex-gap sm:gap-y-20">
              <div class="flex flex-col gap-y-20 w-1/2 md:w-full sm:w-full lg:w-full">
                <div class="specification-title">{{ $t('specifications') }}</div>
                <div>
                  <base-input
                    width="full"
                    height="50"
                    radius="default"
                    inputType="ease"
                    id="device_name"
                    :inputValue="deviceProperties.name"
                    v-model="deviceProperties.name"
                  >
                    <template #text>
                      <span>
                        {{ $t('device_name') }}
                      </span>
                    </template>
                  </base-input>
                </div>
                <div>
                  <base-input
                    width="full"
                    inputType="ease"
                    height="50"
                    radius="default"
                    id="device_serial_number"
                    :disabled="deviceId ? true : false"
                    :inputValue="deviceProperties.serial_number"
                    v-model="deviceProperties.serial_number"
                    @getApiToken="getApiToken(deviceProperties.serial_number)"
                    data-cy="device-serial-number"
                  >
                    <template #text>
                      <span>
                        {{ $t('serial_number') }}
                      </span>
                    </template>
                  </base-input>
                </div>
                <div>
                  <base-search-input
                    :inputPlaceHolder="$t('assign_to_zone')"
                    id="dz_s_device"
                    @selectedItem="getSelectedZone"
                    @searched="getCompanyZones"
                    :default="defaultZone"
                    :itemsToSearch="companyZones"
                    :isLoading="isGettingZones"
                    radius="default"
                  >
                    <template #errorMessage>
                      {{ $t('zone_name_required') }}
                    </template>
                  </base-search-input>
                </div>
                <div>
                  <base-input
                    width="full"
                    inputType="ease"
                    :disabled="true"
                    height="50"
                    radius="default"
                    id="device_api_token"
                    :inputValue="apiToken"
                    v-model="apiToken"
                    :readOnly="true"
                  >
                    <template #text>
                      <span>
                        {{ $t('api_token') }}
                      </span>
                    </template>
                    <template #icon>
                      <Copy class="input-icon" @copyToClipboard="copyToClipboard()" />
                    </template>
                  </base-input>
                </div>
              </div>
              <div class="w-1/2 md:w-full sm:w-full lg:w-full flex flex-col gap-y-20">
                <div class="type-title">{{ $t('type') }}</div>
                <div class="form-box py-15">
                  <div class="flex px-10">
                    <div class="form-check">
                      <span v-for="(type, index) in deviceType" :key="type">
                        <base-radio-button
                          :id="`type-${type}`"
                          :value="index"
                          name="deviceType"
                          :label="$t(`${type}`)"
                          class="pt-10"
                          v-model="deviceProperties.reservable"
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="+deviceProperties.reservable === 1">
                  <base-input
                    width="full"
                    inputType="ease"
                    height="50"
                    radius="default"
                    id="device_capacity"
                    :inputValue="deviceProperties.roomCapacity"
                    v-model="deviceProperties.roomCapacity"
                    :type="'number'"
                  >
                    <template #text>
                      <span>
                        {{ $t('room_capacity') }}
                      </span>
                    </template>
                    <template #icon>
                      <users width="24" height="24" class="mx-12" />
                    </template>
                  </base-input>
                </div>
                <div>
                  <div class="form-box flex" v-if="+deviceProperties.reservable === 1">
                    <div class="self-center w-full">
                      <label for="admin_check" class="switcher-label mx-10 py-10 select-none">
                        <span class="switcher-label-text text-title">
                          {{ $t('reservations_require_confirmation') }}
                        </span>
                        <base-check-box
                          id="admin_check"
                          v-model="deviceProperties.admin_check"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="type-lock-section">
              <div class="flex justify-between md:flex-col sm:flex-col lg:flex-col flex-gap">
                <div class="w-1/2 md:w-full sm:w-full lg:w-full flex flex-col gap-y-20">
                  <div class="type-title">{{ $t('lock') }}</div>
                  <div class="form-box flex">
                    <div class="self-center w-full">
                      <label for="has-lock" class="switcher-label mx-10 py-10 select-none">
                        <span class="switcher-label-text text-title">
                          {{ $t('has_lock') }}
                        </span>
                        <base-check-box
                          id="has-lock"
                          v-model="deviceProperties.has_lock"
                          @change="hasLockHandler"
                        />
                      </label>
                    </div>
                  </div>
                  <div v-if="deviceProperties.has_lock">
                    <base-input
                      width="full"
                      height="50"
                      radius="default"
                      :type="!onShowPassword ? 'password' : 'text'"
                      id="device_password"
                      v-model="deviceProperties.password"
                      :inputValue="deviceProperties.password"
                      @input="passwordValidation"
                    >
                      <template #text>
                        <span class="switcher-label-text">
                          <span v-if="removeDisabled">
                            {{ $t('click_to_change') }}
                          </span>
                          <span v-else>
                            {{ $t('set_password') }}
                          </span>
                        </span>
                      </template>
                      <template #icon>
                        <eye-slash
                          v-if="!onShowPassword"
                          class="input-icon"
                          @click.native="onShowPassword = !onShowPassword"
                        />
                        <eye
                          v-else
                          class="input-icon"
                          @click.native="onShowPassword = !onShowPassword"
                        />
                      </template>
                    </base-input>
                  </div>
                  <div v-if="deviceProperties.has_lock">
                    <div class="form-box flex">
                      <div class="self-center w-full">
                        <label
                          for="has_welcome_message"
                          class="switcher-label mx-10 py-10 select-none"
                        >
                          <span class="switcher-label-text text-title">
                            {{ $t('welcome_switch_text') }}
                          </span>
                          <base-check-box
                            id="has_welcome_message"
                            v-model="deviceProperties.has_welcome_message"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="w-1/2 md:w-full sm:w-full lg:w-full mt-30 flex flex-col gap-y-20">
                  <div v-if="deviceProperties.has_lock" class="mt-16">
                    <div class="form-box flex">
                      <div class="self-center w-full">
                        <label
                          for="unlock-even-offline"
                          class="switcher-label mx-10 py-10 select-none"
                        >
                          <span class="switcher-label-text text-title">
                            {{ $t('unlock_offline') }}
                          </span>
                          <base-check-box
                            id="unlock-even-offline"
                            v-model="deviceProperties.unlock_even_offline"
                          />
                        </label>
                      </div>
                    </div>
                    <p class="text-subtitle text-sm">{{ $t('can_unlock_when_offline') }}</p>
                  </div>
                  <div v-if="deviceProperties.has_lock" class="mt-16">
                    <div class="form-box flex">
                      <div class="self-center w-full">
                        <label
                          for="unlock-by-attendees"
                          class="switcher-label mx-10 py-10 select-none"
                        >
                          <span class="switcher-label-text text-title">
                            {{ $t('unlock_by_attendees') }}
                          </span>
                          <base-check-box
                            id="unlock-by-attendees"
                            v-model="deviceProperties.unlock_by_attendees"
                          />
                        </label>
                      </div>
                    </div>
                    <p class="text-subtitle text-sm">{{ $t('unlock_by_attendees_helper') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="advance-setting-section">
              <div class="advance-setting-title">{{ $t('advanced_settings') }}</div>
              <div class="advanced-setting-container flex-gap">
                <div class="form-box flex w-1/2 h-48 sm:w-full md:w-full lg:w-full">
                  <div class="self-center w-full">
                    <label for="authentication" class="switcher-label mx-10 py-18">
                      <span class="switcher-label-text text-title">
                        {{ $t('need_authentication') }}
                      </span>
                      <base-check-box
                        id="authentication"
                        v-model="deviceProperties.authentication"
                      />
                    </label>
                  </div>
                </div>
                <div class="form-box flex w-1/2 h-48 sm:w-full md:w-full lg:w-full">
                  <div class="self-center w-full">
                    <label for="show_company_logo" class="switcher-label mx-10 py-18">
                      <span class="switcher-label-text text-title">
                        {{ $t('display_company_logo') }}
                      </span>
                      <base-check-box
                        id="show_company_logo"
                        v-model="deviceProperties.logo_display"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div class="advance-setting-form">
                <div class="sm:order-2">
                  <div class="advance-setting-subtitle">{{ $t('theme') }}</div>
                  <div class="form-box">
                    <div class="flex px-10">
                      <div class="form-check">
                        <span v-for="theme in deviceTheme" :key="theme">
                          <base-radio-button
                            :id="`theme-${theme}`"
                            :value="theme"
                            name="deviceTheme"
                            :label="$t(`${theme}`)"
                            class="pt-10"
                            v-model="deviceProperties.theme"
                            @change="setSolidBackground(deviceProperties.theme)"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="grid-column-boxes">
                    <div>
                      <div class="advance-setting-subtitle">{{ $t('screen_saver_menu') }}</div>
                      <base-select
                        class="select-none"
                        height="48"
                        :options="screenSaverTimers"
                        :default="defaultScreenSaver"
                        v-model="deviceProperties.screen_saver_value"
                        radius="default"
                      />
                    </div>
                    <div>
                      <div class="advance-setting-subtitle">{{ $t('calendar_type') }}</div>
                      <base-select
                        class="select-none"
                        height="48"
                        :options="calendars"
                        :default="defaultCalendar"
                        :placeholder="$t('calendar_type')"
                        v-model="deviceProperties.calendar_type"
                        radius="default"
                      />
                    </div>
                    <div>
                      <div class="advance-setting-subtitle">{{ $t('time_zone') }}</div>
                      <base-select-searchable
                        :options="Timezones"
                        @selectedItem="selectedTimeZoneHandler"
                        :placeHolder="$t('time_zone')"
                        :default="defaultTimeZone"
                        id="timeZone"
                        radius="default"
                      />
                    </div>
                    <div>
                      <div class="advance-setting-subtitle">{{ $t('language') }}</div>
                      <base-select
                        class="select-none"
                        height="48"
                        :options="languages"
                        :default="defaultLanguage"
                        :placeholder="$t('language')"
                        v-model="deviceProperties.locale"
                        radius="default"
                      />
                    </div>
                  </div>
                </div>
                <div class="sm:order-1 sm:mb-30">
                  <div class="advance-setting-subtitle">
                    {{ $t('background') }}
                  </div>
                  <div v-if="serverBackgrounds && deviceProperties">
                    <div class="bg-flex-box-top mb-5">
                      <input
                        type="radio"
                        id="light-bg"
                        name="drone"
                        value="light"
                        v-model="deviceProperties.theme"
                        class="hidden"
                        @change="deviceThemeHandler"
                      />
                      <label
                        for="light-bg"
                        class="relative"
                        v-if="deviceProperties.theme === 'light'"
                      >
                        <div class="solid_light" @click="deviceThemeHandler()">
                          {{ $t('light') }}
                        </div>
                        <img
                          :src="primaryCheck"
                          v-if="imageSelector === 'light'"
                          alt="tick"
                          class="solid-icon"
                        />
                      </label>
                      <input
                        type="radio"
                        id="dark-bg"
                        name="drone"
                        value="dark"
                        v-model="deviceProperties.theme"
                        v-if="imageSelector === 'dark'"
                        class="hidden"
                        @change="deviceThemeHandler"
                      />
                      <label
                        for="dark-bg"
                        class="relative"
                        v-if="deviceProperties.theme === 'dark'"
                      >
                        <div class="solid_dark" @click="deviceThemeHandler()">
                          {{ $t('dark') }}
                        </div>
                        <img
                          :src="primaryCheck"
                          v-if="imageSelector === 'dark'"
                          alt="tick"
                          class="solid-icon"
                        />
                      </label>
                      <div class="relative">
                        <div class="h-90">
                          <image-uploader
                            width="full"
                            height="94"
                            uploadIconSize="24"
                            :isEditable="false"
                            :isImageRemoved="removeUserImage"
                            :defaultValue="
                              deviceProperties.background ? deviceProperties.background : ''
                            "
                            @fileUrl="userBackgroundHandler"
                          />
                        </div>
                        <img
                          :src="primaryCheck"
                          v-if="imageSelector === deviceProperties.background"
                          alt="tick"
                          class="solid-icon"
                        />
                      </div>
                    </div>
                    <div class="bg-flex-box-bottom">
                      <div
                        v-for="(item, index) in serverBackgrounds"
                        :key="index"
                        class="w-full h-94 relative"
                        :class="{ hidden: imageSelector === 'item' }"
                      >
                        <div class="solid_img overflow-hidden">
                            <img
                              :src="resizer(item)"
                              class="box-img"
                              :class="[imgError ? 'object-scale-down' : 'object-cover']"
                              @error="imageError($event)"
                              @click="serverBackgroundsHandler(item)"
                            />
                          </div>
                          <img
                            :src="primaryCheck"
                            v-if="imageSelector === item"
                            alt="tick"
                            class="solid-icon"
                          />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="add-button">
              <base-button
                buttonType="primary"
                width="full"
                @click.native="submitDeviceHandler"
                :disabled="isBtnDisabled"
                type="submit"
                data-cy="add-device"
              >
                <template #icon v-if="loadingButton">
                  <base-loading />
                </template>
                <template #text v-else>
                  {{ deviceId ? $t('edit_device') : $t('add_device') }}
                </template>
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { OnImageError } from '@/utils/on-image-error';
import axiosConfig from '@/services/axios.config';
import EyeSlash from '@/assets/icons/vue/eye-slash.vue';
import Eye from '@/assets/icons/vue/eye.vue';
import Copy from '@/assets/icons/vue/copy.vue';
import BackArrow from '@/assets/icons/vue/backArrow.vue';
import primaryCheck from '@/assets/icons/svg/tick-circle-success.svg';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseRadioButton from '@/components/base/BaseRadioButton.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';
import convertToBinary from '@/utils/boolean-converter';
import BaseSearchInput from '@/components/base/BaseSearchInput.vue';
import BaseSelectSearchable from '@/components/base/BaseSelectSearchable.vue';
import timeZones from '@/config/timeZones';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import BaseCheckBox from '@/components/base/BaseCheckBox.vue';
import imageResizer from '@/utils/image-resizer';
import users from '@/assets/icons/vue/users.vue';

let formData = new FormData();

export default {
  components: {
    BaseInput,
    BaseSelect,
    ImageUploader,
    BaseRadioButton,
    BaseSearchInput,
    BaseSelectSearchable,
    BaseButton,
    BaseLoading,
    BackArrow,
    EyeSlash,
    Eye,
    BaseCheckBox,
    Copy,
    users,
  },
  data() {
    return {
      OnImageError,
      primaryCheck,
      deviceProperties: {
        zone_id: '',
        name: '',
        serial_number: '',
        reservable: 0,
        theme: 'light',
        has_lock: false,
        unlock_even_offline: false,
        is_solid: false,
        authentication: false,
        logo_display: false,
        password: '',
        locale: 'fa',
        timezone: 'Asia/Tehran',
        calendar_type: '',
        image_url: '',
        background: '',
        roomCapacity: '',
        admin_check: false,
        screen_saver_value: '',
        unlock_by_attendees: false,
        has_welcome_message: false,
      },
      deviceId: this.$route.params.id,
      languages: [
        { value: 'fa', name: this.$i18n.t('persian') },
        { value: 'en', name: this.$i18n.t('english') },
        { value: 'ar', name: this.$i18n.t('arabic') },
        { value: 'tr', name: this.$i18n.t('turkish') },
        { value: 'de', name: this.$i18n.t('german') },
        { value: 'fr', name: this.$i18n.t('french') },
      ],
      calendars: [
        { value: 'Solar', name: this.$i18n.t('solar_calendar') },
        { value: 'Gregorian', name: this.$i18n.t('gregorian_calendar') },
      ],
      defaultCalendar: {},
      screenSaverTimers: [
        { value: 15, name: `15 ${this.$i18n.t('minute')}` },
        { value: 30, name: `30 ${this.$i18n.t('minute')}` },
        { value: 60, name: `1 ${this.$i18n.t('hour')}` },
        { value: 120, name: `2 ${this.$i18n.t('hour')}` },
        { value: 600, name: `10 ${this.$i18n.t('hour')}` },
        { value: 0, name: this.$i18n.t('never') },
      ],
      defaultScreenSaver: {
        name: `10 ${this.$i18n.t('hour')}`,
        value: 600,
      },
      defaultTimeZone: {
        name: 'Asia/Tehran',
        value: 'Asia/Tehran',
      },
      defaultLanguage: {},
      defaultImage: '',
      defaultZone: {},
      Timezones: timeZones,
      serverBackgrounds: [],
      backgroundSize: 130,
      deviceType: ['work_room', 'conference_room'],
      deviceTheme: ['dark', 'light'],
      isBtnDisabled: false,
      isActiveProgress: false,
      onShowPassword: false,
      isGettingZones: false,
      removeDisabled: false,
      companyZones: [],
      imageSelector: 'light',
      removeUserImage: false,
      imgError: false,
      apiToken: '',
    };
  },
  mounted() {
    this.$store.commit('loading/PAGE_LOADING_HANDLER', true);
    this.initializeData();
    this.clearFormData();
  },
  computed: {
    ...mapGetters({
      defaultCompany: 'getDefaultCompany',
    }),
    isPageLoading() {
      return this.$store.state.loading.pageLoading;
    },
    loadingButton() {
      return this.$store.state.loading.loadingButton;
    },
  },
  methods: {
    resizer(image) {
      return imageResizer(image, this.backgroundSize);
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.apiToken);
      this.$toast.success(this.$t('copied'));
    },
    getApiToken(api) {
      if (this.deviceProperties.serial_number.length !== 0) {
        axiosConfig
          .get(`/companies/${this.defaultCompany.id}/devices/get-api-token/${api}`)
          .then((response) => {
            this.apiToken = response.data.data.api_token;
          })
          .catch((error) => {
            this.apiToken = '';
            throw error;
          });
      } else {
        this.apiToken = '';
      }
    },
    imageError($event) {
      this.imgError = true;
      this.OnImageError($event);
    },
    clearFormData() {
      formData = new FormData();
    },
    async initializeData() {
      await this.getDefaultImages();
      this.getSingleDevice();
      this.$store.commit('loading/PAGE_LOADING_HANDLER', false);
    },
    getCompanyZones(searchedZone) {
      this.isGettingZones = true;
      if (searchedZone.length === 0) {
        this.companyZones = [];
        this.deviceProperties.zone_id = '';
      }
      axiosConfig
        .get(
          `/companies/${this.defaultCompany.id}/zones?per_page=100&q=${searchedZone}&columns[]=name&with_childs=1`,
        )
        .then((response) => {
          this.companyZones = response.data.data.map((zone) => ({
            id: zone.id,
            title: zone.name,
          }));
          this.isGettingZones = false;
        })
        .catch((error) => {
          this.isGettingZones = false;
          throw error;
        });
    },
    getSelectedZone(e) {
      this.deviceProperties.zone_id = e.id;
    },
    async getDefaultImages() {
      return axiosConfig
        .get(`/companies/${this.defaultCompany.id}/devices/get-default-images`)
        .then((response) => {
          this.serverBackgrounds = response.data.success;
        });
    },
    getSingleDevice() {
      if (this.deviceId) {
        axiosConfig
          .get(`/companies/${this.defaultCompany.id}/devices/${this.deviceId}`)
          .then((response) => {
            this.removeDisabled = true;
            const deviceData = response.data.data;
            this.deviceProperties.authentication = deviceData.authentication === 1;
            this.deviceProperties.calendar_type = deviceData.calendar_type;
            this.deviceProperties.locale = deviceData.locale;
            this.deviceProperties.has_lock = deviceData.has_lock === 1;
            this.deviceProperties.unlock_even_offline = deviceData.unlock_even_offline === 1;
            this.deviceProperties.logo_display = deviceData.logo_display === 1;
            this.deviceProperties.name = deviceData.name;
            this.deviceProperties.reservable = deviceData.reservable;
            this.deviceProperties.serial_number = deviceData.serial_number;
            this.deviceProperties.theme = deviceData.theme;
            this.deviceProperties.timezone = deviceData.timezone;
            this.deviceProperties.zone_id = deviceData.zone_id;
            this.deviceProperties.admin_check = deviceData.admin_check === 1;
            this.deviceProperties.unlock_by_attendees = deviceData.unlock_by_attendees === 1;
            this.deviceProperties.has_welcome_message = deviceData.has_welcome_message === 1;
            this.deviceProperties.screen_saver_value = deviceData.screen_saver_value;
            if (deviceData.capacity === null) {
              this.deviceProperties.roomCapacity = '';
            } else {
              this.deviceProperties.roomCapacity = deviceData.capacity;
            }
            this.getApiToken(deviceData.serial_number);
            if (deviceData.background) {
              this.defaultImage = deviceData.background;
              this.checkDefaultImageHandler();
            } else if (deviceData.image_url) {
              this.defaultImage = deviceData.image_url;
              this.deviceProperties.image_url = deviceData.image_url;
              this.checkDefaultImageHandler();
            } else {
              this.imageSelector = this.deviceProperties.theme === 'light' ? 'light' : 'dark';
            }
            if (deviceData.zone) {
              this.defaultZone = {
                title: deviceData.zone.name,
                id: deviceData.zone.id,
              };
            }
            this.defaultCalendar = {
              name: this.calendars.find((o) => o.value === this.deviceProperties.calendar_type)
                .name,
              value: this.deviceProperties.calendar_type,
            };
            this.defaultTimeZone = {
              name: this.deviceProperties.timezone,
              value: this.deviceProperties.timezone,
            };
            this.defaultLanguage = {
              name: this.languages.find((o) => o.value === this.deviceProperties.locale).name,
              value: this.deviceProperties.locale,
            };
            this.defaultScreenSaver = {
              name: this.screenSaverTimers.find(
                (o) => o.value === this.deviceProperties.screen_saver_value,
              ).name,
              value: this.deviceProperties.screen_saver_value,
            };
          });
      } else {
        this.defaultCalendar = this.calendars[0];
        this.defaultLanguage = this.languages[0];
      }
    },
    checkDefaultImageHandler() {
      if (this.serverBackgrounds.includes(this.defaultImage)) {
        this.deviceProperties.image_url = this.defaultImage;
        this.imageSelector = this.defaultImage;
      } else {
        this.deviceProperties.image_url = '';
        this.deviceProperties.background = this.defaultImage;
        this.imageSelector = this.deviceProperties.background;
      }
    },
    submitDeviceHandler() {
      this.isBtnDisabled = true;
      this.removeAllFormData();
      this.$store.commit('loading/LOADING_HANDLER', true);
      formData.append('name', this.deviceProperties.name);
      formData.append('serial_number', this.deviceProperties.serial_number);
      formData.append('reservable', this.deviceProperties.reservable);
      formData.append('calendar_type', this.deviceProperties.calendar_type);
      formData.append('timezone', this.deviceProperties.timezone);
      formData.append('locale', this.deviceProperties.locale);
      formData.append('has_lock', convertToBinary(this.deviceProperties.has_lock));
      formData.append(
        'unlock_even_offline',
        convertToBinary(this.deviceProperties.unlock_even_offline),
      );
      formData.append('authentication', convertToBinary(this.deviceProperties.authentication));
      formData.append('logo_display', convertToBinary(this.deviceProperties.logo_display));
      formData.append('theme', this.deviceProperties.theme);
      if (this.deviceProperties.zone_id) {
        formData.append('zone_id', this.deviceProperties.zone_id);
      }
      if (this.deviceProperties.is_solid) {
        formData.append('is_solid', this.deviceProperties.is_solid);
      }
      if (
        this.deviceProperties.background
        && typeof this.deviceProperties.background !== 'string'
      ) {
        formData.append('background', this.deviceProperties.background);
      }
      if (this.deviceProperties.image_url) {
        formData.append('image_url', this.deviceProperties.image_url);
      }
      if (this.deviceProperties.has_lock && this.deviceProperties.password) {
        formData.append('password', this.deviceProperties.password);
      }
      formData.append('capacity', this.deviceProperties.roomCapacity);
      formData.append('screen_saver_value', this.deviceProperties.screen_saver_value);
      formData.append('admin_check', convertToBinary(this.deviceProperties.admin_check));
      formData.append(
        'unlock_by_attendees',
        convertToBinary(this.deviceProperties.unlock_by_attendees),
      );
      formData.append(
        'has_welcome_message',
        convertToBinary(this.deviceProperties.has_welcome_message),
      );
      if (this.deviceId) {
        formData.append('_method', 'PUT');
        this.editDevice(formData);
      } else {
        this.setNewDevice(formData);
      }
    },
    setNewDevice(deviceData) {
      axiosConfig
        .post(`/companies/${this.defaultCompany.id}/devices`, deviceData)
        .then(() => {
          this.$toast.success(this.$t('device_added'));
          this.isBtnDisabled = false;
          this.$store.commit('loading/LOADING_HANDLER', false);
          this.$router.push({ name: 'DevicesPage' });
        })
        .catch((error) => {
          serverErrorsHandler(error);
          this.isBtnDisabled = false;
          this.$store.commit('loading/LOADING_HANDLER', false);
          throw error;
        });
    },
    editDevice(deviceData) {
      axiosConfig
        .post(`/companies/${this.defaultCompany.id}/devices/${this.deviceId}`, deviceData)
        .then(() => {
          this.$toast.success(this.$t('device_edited'));
          this.isBtnDisabled = false;
          this.$store.commit('loading/LOADING_HANDLER', false);
          // eslint-disable-next-line
          if (this.deviceProperties.reservable == 0) {
            this.$router.push({ name: 'WorkRoom', params: { id: this.deviceId } });
            // eslint-disable-next-line
          } else if (this.deviceProperties.reservable == 1) {
            this.$router.push({ name: 'ConferenceRoom', params: { id: this.deviceId } });
          }
        })
        .catch((error) => {
          serverErrorsHandler(error);
          this.isBtnDisabled = false;
          this.$store.commit('loading/LOADING_HANDLER', false);
          throw error;
        });
    },
    userBackgroundHandler(e) {
      this.removeUserImage = false;
      this.deviceProperties.is_solid = false;
      this.deviceProperties.background = e;
      this.imageSelector = e;
      this.deviceProperties.image_url = '';
      formData.delete('image_url');
      formData.delete('is_solid');
    },
    serverBackgroundsHandler(item) {
      this.removeUserImage = true;
      this.deviceProperties.is_solid = false;
      this.imageSelector = item;
      this.deviceProperties.image_url = item;
      formData.delete('background');
      this.deviceProperties.background = '';
    },
    deviceThemeHandler() {
      this.removeUserImage = true;
      this.deviceProperties.is_solid = true;
      this.imageSelector = this.deviceProperties.theme;
      formData.delete('image_url');
      formData.delete('background');
      this.deviceProperties.background = '';
      this.deviceProperties.image_url = '';
      this.removeUserImage = false;
    },
    setSolidBackground(theme) {
      this.removeUserImage = true;
      this.deviceProperties.is_solid = true;
      if (this.imageSelector === 'light' || this.imageSelector === 'dark') {
        this.imageSelector = theme;
      }
      formData.delete('image_url');
      formData.delete('background');
      formData.delete('is_solid');
      this.deviceProperties.image_url = '';
      this.deviceProperties.background = '';
    },
    passwordValidation() {
      this.deviceProperties.password = this.deviceProperties.password.replace(/[^0-9]/g, '');
    },
    hasLockHandler() {
      if (!this.deviceProperties.has_lock) {
        this.deviceProperties.password = null;
        formData.delete('password');
      }
    },
    selectedTimeZoneHandler(timeZone) {
      this.deviceProperties.timezone = timeZone.name;
    },
    removeAllFormData() {
      const keys = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const key of formData.keys()) {
        if (key) {
          keys.push(key);
        }
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const idx in keys) {
        if (idx) {
          formData.delete(keys[idx]);
        }
      }
    },
  },
};
</script>
<style scoped>
.container-padding {
  @apply px-186 sm:px-10 md:px-10 lg:px-10;
}
.advanced-setting-container {
  @apply flex sm:flex-col md:flex-col lg:flex-col
   sm:gap-y-20 md:gap-y-20 lg:gap-y-20 w-full justify-between mb-30;
}
.flex-gap {
  @apply gap-x-78;
}
.back-button {
  @apply w-50 h-50 rounded flex border-2 border-default cursor-pointer left-50
  sm:left-32;
}
.page-title {
  @apply text-24 font-semibold text-title;
}
.page-subtitle {
  @apply text-12 text-subtitle;
}
.card-grid-box {
  @apply grid grid-cols-2 md:grid-cols-1 sm:grid-cols-1 gap-x-78;
}
.specification-section,
.type-lock-section {
  @apply mt-50;
}
.advance-setting-section {
  @apply mt-102;
}
.advance-setting-title,
.type-title {
  @apply text-18 font-semibold text-subtitle;
}
.specification-title {
  @apply text-24 font-bold text-title;
}
.advance-setting-subtitle {
  @apply text-18 font-semibold text-title my-10;
}
.advance-setting-form {
  @apply card-grid-box;
}
.form-box {
  @apply border border-default bg-secondary rounded scale-125;
}
.form-check {
  @apply my-10 cursor-pointer flex flex-col items-start pb-10;
}
.bg-flex-box-top {
  @apply grid grid-cols-2 md:grid-cols-2 sm:grid-cols-2 gap-5 gap-x-15;
}
.bg-flex-box-bottom {
  @apply grid grid-cols-3 md:grid-cols-2 sm:grid-cols-2 gap-15 mt-10;
}
.solid_img,
.solid_light,
.solid_dark {
  @apply rounded-sm h-94 w-full flex-center font-semibold text-sm
    cursor-pointer;
}
.solid_light {
  @apply border border-default bg-primary text-black;
  background-color: #ffffff;
}
.solid_dark {
  @apply text-white shadow-md;
  background-color: #21213c;
}
.solid-icon {
  @apply absolute bottom-10 right-10;
}
.solid-icon-show_more {
  @apply absolute top-38 left-1/2 transform -translate-y-1/2 -translate-x-1/2;
}
.solid-text-show_more {
  @apply absolute top-58 left-1/2 transform -translate-y-1/2 -translate-x-1/2
    text-14 text-white font-semibold;
}
.grid-column-boxes {
  @apply grid grid-cols-1 gap-10;
}
.box-img {
  @apply rounded-sm w-full;
}
.add-button {
  @apply py-50;
}
.input-icon {
  @apply absolute top-/29 cursor-pointer select-none;
}
.input-icon:is(:lang(fa), :lang(ar)) {
  @apply left-20;
}
.input-icon {
  @apply right-20;
}
.flex-center {
  @apply flex items-center justify-center;
}
</style>
